const MAIN_SITE_HOST = 'harday.co.in';
const ADMIN_SITE_HOST = 'admin.harday.co.in';
const LEGACY_ADMIN_PATH = '/harday-admin';
const ADMIN_PATH = '/admin';

const getLocation = () => (typeof window !== 'undefined' ? window.location : null);

const sanitizePath = (value) => {
  if (!value || typeof value !== 'string') return '';
  if (!value.startsWith('/')) return '';
  return value.split('#')[0];
};

export const isAdminPath = (value) => {
  const path = sanitizePath(value);
  return path === ADMIN_PATH || path === LEGACY_ADMIN_PATH;
};

export const getCurrentHostname = () => getLocation()?.hostname || '';

export const isProductionHardayHost = () => {
  const hostname = getCurrentHostname();
  return hostname === MAIN_SITE_HOST || hostname === `www.${MAIN_SITE_HOST}` || hostname === ADMIN_SITE_HOST;
};

export const isAdminSubdomainHost = () => getCurrentHostname() === ADMIN_SITE_HOST;

// Preview/dev hosts where the admin UI should still be accessible for testing
// without needing the production admin.harday.co.in subdomain. Mirrors the
// backend's enforce_admin_site_request allowlist so admin flows can be
// exercised end-to-end in preview environments.
const ADMIN_PREVIEW_HOST_SUFFIXES = [
  '.preview.emergentagent.com',
  '.preview.emergentcf.cloud',
  '.emergentagent.com',
  '.emergentcf.cloud',
];

export const isAdminUIHostAllowed = () => {
  const hostname = getCurrentHostname();
  if (hostname === ADMIN_SITE_HOST) return true;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true;
  return ADMIN_PREVIEW_HOST_SUFFIXES.some((suffix) => hostname.endsWith(suffix));
};

const buildOrigin = (host) => {
  const location = getLocation();
  if (!location) return '';
  return `${location.protocol}//${host}`;
};

export const getMainSiteOrigin = () => {
  if (!isProductionHardayHost()) {
    return getLocation()?.origin || '';
  }
  return buildOrigin(MAIN_SITE_HOST);
};

export const getAdminSiteOrigin = () => {
  if (!isProductionHardayHost()) {
    return getLocation()?.origin || '';
  }
  return buildOrigin(ADMIN_SITE_HOST);
};

export const canRenderAdminInline = () => !isProductionHardayHost() || isAdminSubdomainHost();

export const getAdminPanelUrl = (path = ADMIN_PATH) => {
  const normalizedPath = isAdminPath(path) ? ADMIN_PATH : sanitizePath(path) || ADMIN_PATH;
  return `${getAdminSiteOrigin()}${normalizedPath}`;
};

export const getMainSiteUrl = (path = '/') => {
  const normalizedPath = sanitizePath(path) || '/';
  return `${getMainSiteOrigin()}${normalizedPath}`;
};

export const getRootEntryPath = () => (isAdminSubdomainHost() ? ADMIN_PATH : '/login');

export const getPostLoginPath = ({ nextPath = '', isAdminUser = false } = {}) => {
  const normalizedNextPath = sanitizePath(nextPath);

  if (isAdminPath(normalizedNextPath)) {
    return isAdminUser ? ADMIN_PATH : '/dashboard';
  }

  if (normalizedNextPath) {
    return normalizedNextPath;
  }

  if (isAdminSubdomainHost() && isAdminUser) {
    return ADMIN_PATH;
  }

  return '/dashboard';
};
